import { CircleCheck, CircleX, LockKeyhole, Radio } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getAuthToken, getAuthUser, subscribeAuthStateChange } from "@/lib/api/token-store";
import { getSessionLockDeadline, subscribeSessionLockDeadline } from "@/lib/auth/session-lock";
import { subscribeChatConnectionStatus } from "@/lib/realtime/chat-realtime-service";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

function formatRemaining(ms) {
  const totalSeconds = Math.max(0, Math.ceil(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  }
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

/**
 * Countdown to the idle auto-lock (the user's name used to sit here — it already lives on the
 * sidebar profile chip). Hidden when logged out or when auto-lock is disabled; the deadline
 * re-arms in ≤5s steps while the user is active, so the timer visibly resets as they work.
 */
function SessionLockCountdown() {
  const [deadline, setDeadline] = useState(() => getSessionLockDeadline());
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => subscribeSessionLockDeadline(setDeadline), []);
  useEffect(() => {
    if (deadline == null) return undefined;
    setNowMs(Date.now());
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [deadline]);

  if (deadline == null) return null;
  const remainingMs = deadline - nowMs;
  const isClose = remainingMs <= 60_000;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-flex items-center gap-1.5 tabular-nums ${isClose ? "text-amber-500" : ""}`}
          >
            <LockKeyhole className="h-3.5 w-3.5" />
            <span>{formatRemaining(remainingMs)}</span>
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">
          Auto-locks after inactivity — {formatRemaining(remainingMs)} left. Any activity resets it.
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function getCurrentAuthState() {
  const isLoggedIn = Boolean(getAuthToken());
  const user = getAuthUser();
  const userLabel = String(user?.name || user?.loginId || user?.id || "").trim();

  return {
    isLoggedIn,
    userLabel,
  };
}

function StatusBar() {
  const [authState, setAuthState] = useState(() => getCurrentAuthState());
  const [chatConnectionStatus, setChatConnectionStatus] = useState("disconnected");

  useEffect(() => {
    const syncAuthState = () => {
      setAuthState(getCurrentAuthState());
    };

    const unsubscribe = subscribeAuthStateChange(syncAuthState);
    return unsubscribe;
  }, []);

  useEffect(() => {
    return subscribeChatConnectionStatus(setChatConnectionStatus);
  }, []);

  const statusLabel = useMemo(() => {
    if (!authState.isLoggedIn) {
      return "Not logged in";
    }

    if (authState.userLabel) {
      return `Logged in as ${authState.userLabel}`;
    }

    return "Logged in";
  }, [authState.isLoggedIn, authState.userLabel]);

  const chatStatusLabel = useMemo(() => {
    if (!authState.isLoggedIn) {
      return "Chat: offline (not signed in)";
    }
    const normalized = String(chatConnectionStatus || "disconnected").toLowerCase();
    const labels = {
      disconnected: "Chat: disconnected",
      connecting: "Chat: connecting…",
      connected: "Chat: connected",
      reconnecting: "Chat: reconnecting…",
      error: "Chat: connection error",
    };
    return labels[normalized] || `Chat: ${normalized}`;
  }, [authState.isLoggedIn, chatConnectionStatus]);

  const chatIconClass = useMemo(() => {
    if (!authState.isLoggedIn) {
      return "text-muted-foreground";
    }
    const normalized = String(chatConnectionStatus || "").toLowerCase();
    if (normalized === "connected") {
      return "text-emerald-500";
    }
    if (normalized === "connecting" || normalized === "reconnecting") {
      return "text-amber-500";
    }
    if (normalized === "error") {
      return "text-rose-500";
    }
    return "text-muted-foreground";
  }, [authState.isLoggedIn, chatConnectionStatus]);

  return (
    <footer className="flex h-10 items-center justify-between border-t border-border bg-card/70 px-4 text-xs text-muted-foreground">
      <div className="flex items-center gap-3">
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              {authState.isLoggedIn ? <CircleCheck className="h-3.5 w-3.5 text-emerald-500" /> : <CircleX className="h-3.5 w-3.5 text-rose-500" />}
            </TooltipTrigger>
            <TooltipContent side="top">{statusLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <TooltipProvider delayDuration={150}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex items-center gap-1.5">
                <Radio className={`h-3.5 w-3.5 ${chatIconClass}`} />
                <span className="hidden sm:inline">{chatStatusLabel}</span>
              </span>
            </TooltipTrigger>
            <TooltipContent side="top">{chatStatusLabel}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <SessionLockCountdown />
    </footer>
  );
}

export default StatusBar;
